import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal, Select, TextInput, Theme } from '../../../components';
import { Role, Store, User } from '../../../models';
import { storeActions, storeSelectors, usersActions, userSelectors, usersSelectors } from '../../../redux';

import './AddEditStore.scss';

const RIGHTS_TO_ROLES = {
  [Role.APP_ADMIN]: [Role.APP_ADMIN, Role.RESTO_ADMIN, Role.FRONT_OFFICE_USER],
  [Role.RESTO_ADMIN]: [Role.RESTO_ADMIN, Role.FRONT_OFFICE_USER],
  [Role.FRONT_OFFICE_USER]: [Role.FRONT_OFFICE_USER],
};

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  user: User;
}

const AddEditUser: FC<Props> = ({ isOpen, closeModal, user }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isAddEditLoading: boolean = useSelector(usersSelectors.isAddEditLoading);
  const stores: Store[] = useSelector(storeSelectors.getStores);
  const userInfo: User = useSelector(userSelectors.getUserInfo);

  const [email, setEmail] = useState<string>(user?.email || '');
  const [firstName, setFirstName] = useState<string>(user?.firstName || '');
  const [lastName, setLastName] = useState<string>(user?.lastName || '');
  const [storeId, setStoreId] = useState<string>(user?.store?.id || '');
  const [rights, setRights] = useState<Role | ''>(user?.roles[0] || '');

  const handleCloseModal = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setStoreId('');
    setRights('');
    closeModal();
  };

  useEffect(() => {
    // TODO: this should not be paginated??
    dispatch(storeActions.getStoresAndDepartments({ offset: 0, limit: 100 }));
  }, []);

  useEffect(() => {
    setEmail(user?.email || '');
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setStoreId(user?.store?.id || '');
    setRights(user?.roles[0] || '');
  }, [user]);

  useEffect(() => {
    if (userInfo && !user) {
      setStoreId(userInfo?.store?.id);
    }
  }, [userInfo, user]);

  useEffect(() => {
    if (rights === Role.APP_ADMIN) {
      setStoreId('');
    }
  }, [rights]);

  useEffect(() => {
    if (isOpen) setStoreId(userInfo?.store?.id);
  }, [isOpen]);

  useEffect(() => {
    if (!isAddEditLoading) {
      handleCloseModal();
    }
  }, [isAddEditLoading]);

  const save = () => {
    const newUser = {
      email,
      firstName: firstName || null,
      lastName: lastName || null,
      store: storeId ? { id: storeId } : null,
      roles: RIGHTS_TO_ROLES[rights],
      state: 'ACTIVE',
      countryId: userInfo.country.id,
    };
    if (user) {
      dispatch(usersActions.editUser(user.id, newUser));
    } else {
      dispatch(usersActions.addUser(newUser));
    }
  };

  const getRightOptions = () => {
    const rights = [
      {
        label: 'lblRestoAdmin',
        value: Role.RESTO_ADMIN,
      },
      {
        label: 'lblFrontOfficeUser',
        value: Role.FRONT_OFFICE_USER,
      },
    ];
    if (userInfo?.roles?.includes(Role.APP_ADMIN)) {
      rights.unshift({
        label: 'lblAppAdmin',
        value: Role.APP_ADMIN,
      });
    }
    return rights;
  };

  const getStoreOptions = () => {
    let selectableStores;

    if (userInfo?.roles.includes(Role.APP_ADMIN)) {
      selectableStores = stores;
    } else {
      selectableStores = [userInfo.store];
    }

    return selectableStores.map((store: Store) => ({
      value: store.id,
      label: store.name,
    }));
  };

  return (
    <Modal title={t(user ? 'lblEditUser' : 'lblAddUser')} isOpen={isOpen} closeModal={() => closeModal()}>
      <div className='add-edit-container'>
        <TextInput label={t('lblEmail')} placeholder={t('phEmail')} onChange={setEmail} value={email} disabled={!!user} />
        <Select
          label='lblRights'
          placeholder='phRights'
          options={getRightOptions()}
          onChange={value => setRights(value as Role | '')}
          value={rights}
        />
        <Select
          label='lblStore'
          placeholder='phStore'
          options={getStoreOptions()}
          onChange={setStoreId}
          value={storeId}
          disabled={!userInfo?.roles?.includes(Role.APP_ADMIN) || rights === Role.APP_ADMIN}
        />
        <TextInput label={t('lblFirstName')} placeholder={t('phFirstName')} onChange={setFirstName} value={firstName} />
        <TextInput label={t('lblLastName')} placeholder={t('phLastName')} onChange={setLastName} value={lastName} />

        <div className='buttons'>
          <Button theme={Theme.INVERSE} onClickCallback={handleCloseModal}>
            {t('lblCancel')}
          </Button>
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={save}
            isLoading={isAddEditLoading}
            isDisabled={!email || !rights || (rights !== Role.APP_ADMIN && !storeId)}>
            {t('lblSave')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditUser;
